import type { IFileItem } from '../models/FileItem';
import { fileBaseName, stripCopyCounter } from '../rules/ruleUtils';
import { getPoorFilenameReason } from '../rules/FilenameRule';

const VERSION_WORDS: RegExp = /(^|[\s_\-.])(v\d+|final|new|latest|really|updated)(?=$|[\s_\-.])/gi;

/**
 * Clean-up-only rename: strips "Copy of", "(1)" counters and version words, then tidies
 * separators. Returns undefined when the result still isn't a descriptive name — that
 * file needs a human (or, later, AI) to name it.
 */
export function cleanUpFileName(file: IFileItem): string | undefined {
  let base: string = fileBaseName(file);
  base = base.replace(/^copy of\s+/i, '');
  base = stripCopyCounter(base);
  base = base.replace(VERSION_WORDS, ' ');
  base = base.replace(/[_]+/g, ' ').replace(/\s*-\s*$/, '').replace(/^\s*-\s*/, '');
  base = base.replace(/\s+/g, ' ').trim();

  if (!base) {
    return undefined;
  }
  const cleaned: string = file.extension ? `${base}.${file.extension}` : base;
  if (cleaned === file.name) {
    return undefined;
  }
  const candidate: IFileItem = { ...file, name: cleaned };
  return getPoorFilenameReason(candidate) ? undefined : cleaned;
}

const INVALID_CHARS: RegExp = /["*:<>?/\\|]/;
const RESERVED_NAMES: RegExp = /^(con|prn|aux|nul|com\d|lpt\d|\.lock|desktop\.ini|_vti_)$/i;

/**
 * SharePoint Online naming rules. Returns an error message, or undefined when valid.
 * requiredExtension: the original extension, which a rename must keep.
 */
export function validateItemName(name: string, requiredExtension?: string): string | undefined {
  if (!name.trim()) {
    return 'Enter a name.';
  }
  if (name !== name.trim()) {
    return 'Names can\'t start or end with a space.';
  }
  if (INVALID_CHARS.test(name)) {
    return 'Names can\'t contain " * : < > ? / \\ |';
  }
  if (/\.$/.test(name)) {
    return 'Names can\'t end with a period.';
  }
  if (/^~\$/.test(name) || /_vti_/i.test(name)) {
    return 'Names can\'t start with ~$ or contain _vti_.';
  }
  const base: string = name.replace(/\.[^.]*$/, '');
  if (RESERVED_NAMES.test(name) || RESERVED_NAMES.test(base)) {
    return `"${name}" is reserved by SharePoint.`;
  }
  if (name.length > 255) {
    return 'Names can be at most 255 characters.';
  }
  if (requiredExtension !== undefined) {
    const dot: number = name.lastIndexOf('.');
    const extension: string = dot > 0 ? name.substring(dot + 1).toLowerCase() : '';
    if (extension !== requiredExtension) {
      return requiredExtension
        ? `Keep the .${requiredExtension} extension so the file still opens correctly.`
        : 'Don\'t add an extension to this file.';
    }
  }
  return undefined;
}
